// React
import React, { useState, useEffect, useRef, useMemo } from 'react';
// External libs
import classNames from 'classnames';
import { get } from 'lodash';
// Material UI
import { Button } from '@material-ui/core';
import { Button as ButtonProps } from './interfaces';
// Consts
import { Colors, Variants } from './consts';
import { Serverities } from '../../../../constants/Severities';
// Components
import CustomLoader from '../../custom-loader';
// Styles
import useStyles, { useStyleRules } from './styles';

const CustomButton: React.FC<ButtonProps> = ({
  children,
  className,
  classes,
  color,
  styleRules,
  disabled,
  fullWidth,
  isLoading,
  loaderSize,
  onClick = () => {},
  text,
  type,
  variant,
}) => {
  const componentClasses = useStyles({ fullWidth });
  const styleRulesClasses = useStyleRules({ styleRules });

  const [isLoadingLocal, setIsLoadingLocal] = useState(isLoading);
  const [localColor, setLocalColor] = useState<Colors | undefined>(undefined);

  const _isMounted = useRef(true); // Initial value _isMounted = true

  const severitySubmitButtonClass = useMemo(() => {
    switch (color) {
      case Serverities.INFO:
        return componentClasses.infoSubmitButton;
      case Serverities.WARNING:
        return componentClasses.warningSubmitButton;
      case 'tertiary':
        return componentClasses.tertiarySubmitButton;
      default:
        return null;
    }
  }, [color, componentClasses]);

  useEffect(() => {
    const colorToSet =
      color === 'primary' || color === 'secondary' ? color : undefined;
    setLocalColor(colorToSet);
  }, [color]);

  useEffect(() => {
    setIsLoadingLocal(isLoading);
  }, [isLoading]);

  useEffect(() => {
    return () => {
      // ComponentWillUnmount
      _isMounted.current = false;
    };
  }, []);

  return (
    <div
      className={classNames(
        componentClasses.root,
        get(classes, 'root', null),
        styleRulesClasses.root,
      )}
    >
      <Button
        className={className}
        type={type}
        disabled={disabled || isLoadingLocal}
        fullWidth={fullWidth}
        variant={variant}
        color={localColor}
        classes={{
          root: classNames(
            componentClasses.submitButton,
            severitySubmitButtonClass,
            get(classes, 'submitButton', null),
            styleRulesClasses.submitButton,
          ),
          disabled: classNames(
            componentClasses.disabledSubmitButton,
            get(classes, 'disabledSubmitButton', null),
            styleRulesClasses.disabledSubmitButton,
          ),
        }}
        onClick={async e => {
          try {
            setIsLoadingLocal(true);
            await onClick(e);
          } catch (ex) {
            console.error(ex);
          }

          if (_isMounted.current) {
            setIsLoadingLocal(false);
          }
        }}
      >
        {text || children}
      </Button>
      <CustomLoader invisible open={isLoadingLocal} size={loaderSize} />
    </div>
  );
};
CustomButton.defaultProps = {
  // To avoid possible React failure.
  children: '',
  classes: {},
  color: Colors.PRIMARY,
  disabled: false,
  fullWidth: true,
  isLoading: false,
  loaderSize: 24,
  onClick: () => {},
  text: '',
  variant: Variants.TEXT,
};
export default CustomButton;
